import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSocialShareModal } from 'containers/SocialShareModal/SocialShareModalContext';
import useOnShare from 'containers/SocialShareModal/useOnShare';
import useInstagramAuthenticator from 'hooks/useInstagramAuthenticator';
import {
  scheduleInstagramVideoToFeed,
  scheduleInstagramVideoToStory,
  shareInstagramVideoToFeed,
  shareInstagramVideoToStory,
} from 'redux/modules/download';
import {
  onClickInstagramPostModalCTA,
  onInstagramPostModalLoad,
} from 'redux/modules/mixpanel';
import { instagramSelector } from 'redux/modules/social';
import { Dispatch } from 'redux/types';
import { SocialSharePlatformData } from '../types';
import { useInstagramPostComposerStep } from './steps';
import { InstagramPostType, InstagramSocialShareStep } from './types';

export default function useInstagramSocialShare(): SocialSharePlatformData<
  InstagramSocialShareStep
> {
  const [postType, setPostType] = useState<InstagramPostType>('feed');

  const { providerUserId, profileImageUrl, username, postStatus } = useSelector(
    instagramSelector,
  );

  const dispatch = useDispatch<Dispatch>();

  const { description, onAuthSuccess, onAuthError } = useSocialShareModal();

  const { authenticating, authenticate } = useInstagramAuthenticator({
    force: true,
    onAuthSuccess: () => {
      onAuthSuccess();
      dispatch(onInstagramPostModalLoad('AccountsExist'));
    },
    onError: onAuthError,
  });

  const handlePostToFeed = useOnShare({
    shareNow: useCallback(() => {
      dispatch(onClickInstagramPostModalCTA('PostReel'));
      dispatch(shareInstagramVideoToFeed(description));
    }, [description, dispatch]),
    schedule: useCallback(
      (scheduledDate: number) =>
        dispatch(scheduleInstagramVideoToFeed(description, scheduledDate)),
      [description, dispatch],
    ),
  });

  const handlePostAsStory = useOnShare({
    shareNow: useCallback(() => dispatch(shareInstagramVideoToStory()), [
      dispatch,
    ]),
    schedule: useCallback(
      (scheduledDate: number) =>
        dispatch(scheduleInstagramVideoToStory(scheduledDate)),
      [dispatch],
    ),
  });

  const handleShare = useCallback(() => {
    if (postType === 'feed') {
      handlePostToFeed();
    } else {
      handlePostAsStory();
    }
  }, [handlePostAsStory, handlePostToFeed, postType]);

  const instagramPostComposerStep = useInstagramPostComposerStep({
    postType,
    onPostTypeChange: setPostType,
    onShare: handleShare,
  });

  return {
    platformAlias: 'instagram',
    authenticating: authenticating || !username,
    accounts: [
      {
        id: providerUserId,
        avatarSrc: profileImageUrl,
        username,
        handle: username,
      },
    ],
    steps: [instagramPostComposerStep],
    defaultStep: instagramPostComposerStep.id,
    postStatus,
    authenticate,
  };
}
